import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./tailwind.generated.css";
import "./style.css";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { createHandoffContainer, prerenderedHtmlMatchesUrl } from "./utils/prerenderHandoff";

// Lazy load pages for code splitting
const HomePage = lazy(() => import("./pages/HomePage").then(module => ({ default: module.HomePage })));
const RankingsPage = lazy(() => import("./pages/RankingsPage").then(module => ({ default: module.RankingsPage })));
const CollegeDetailPage = lazy(() => import("./pages/CollegeDetailPage").then(module => ({ default: module.CollegeDetailPage })));
const CollegeCareerPage = lazy(() => import("./pages/CollegeCareerPage").then(module => ({ default: module.CollegeCareerPage })));
const ConferenceCareerPage = lazy(() => import("./pages/ConferenceCareerPage").then(module => ({ default: module.ConferenceCareerPage })));
const About = lazy(() => import("./pages/About").then(module => ({ default: module.About })));
const Methodology = lazy(() => import("./pages/Methodology").then(module => ({ default: module.Methodology })));
const PlayersPage = lazy(() => import("./pages/PlayersPage").then(module => ({ default: module.PlayersPage })));
const PlayersCareerPage = lazy(() => import("./pages/PlayersCareerPage").then(module => ({ default: module.PlayersCareerPage })));
const ClassesPage = lazy(() => import("./pages/ClassesPage").then(module => ({ default: module.ClassesPage })));
const ClassDetailPage = lazy(() => import("./pages/ClassDetailPage").then(module => ({ default: module.ClassDetailPage })));

// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { firebaseConfig } from "./config/firebase";

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

function App() {

  return (
    <HelmetProvider>
      <Router>
      <div
        style={{
          height: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#0B1220",
        }}
      >
        <Header />
        <main
          style={{
            paddingLeft: "25px",
            paddingRight: "25px",
            flex: "1 1 auto",
            minHeight: 0,
          }}
          className="main-content-wrapper"
        >
          <Suspense fallback={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '400px',
              color: 'white',
              width: '100%',
              height: '100%'
            }}>
              Loading...
            </div>
          }>
            <Routes>
              {/* Homepage - serves actual content for SEO */}
              <Route path="/" element={<HomePage />} />
              
              {/* SEO-friendly rankings routes */}
              {/* Conference routes - must come before non-conference routes */}
              {/* Conference team routes with abbreviation (most specific first) */}
              <Route path="/rankings/:statCategory/:year/week/:week/conference/:conferenceAbbr" element={<RankingsPage />} />
              <Route path="/rankings/:statCategory/:year/conference/:conferenceAbbr" element={<RankingsPage />} />
              <Route path="/rankings/:year/week/:week/conference/:conferenceAbbr" element={<RankingsPage />} />
              <Route path="/rankings/:year/conference/:conferenceAbbr" element={<RankingsPage />} />
              <Route path="/rankings/:statCategory/conference/:conferenceAbbr" element={<RankingsPage />} />
              <Route path="/rankings/conference/:conferenceAbbr" element={<RankingsPage />} />
              {/* Conference routes without abbreviation */}
              <Route path="/rankings/:statCategory/:year/week/:week/conference" element={<RankingsPage />} />
              <Route path="/rankings/:statCategory/:year/conference" element={<RankingsPage />} />
              <Route path="/rankings/:year/week/:week/conference" element={<RankingsPage />} />
              <Route path="/rankings/:year/conference" element={<RankingsPage />} />
              <Route path="/rankings/:statCategory/conference" element={<RankingsPage />} />
              <Route path="/rankings/conference" element={<RankingsPage />} />
              
              {/* Non-conference routes */}
              {/* Most specific routes first (week routes) */}
              <Route path="/rankings/:statCategory/:year/week/:week" element={<RankingsPage />} />
              {/* Position path: /rankings/{stat}/{year}/{pos} (e.g. /rankings/receiving/2025/wr) */}
              <Route path="/rankings/:statCategory/:year/:position" element={<RankingsPage />} />
              {/* Year routes with statCategory — also matches /rankings/{stat}/{pos} when pos is not a year */}
              <Route path="/rankings/:statCategory/:year" element={<RankingsPage />} />
              {/* Fantasy Points week routes without statCategory (must come before /rankings/:statCategory to avoid conflicts) */}
              <Route path="/rankings/:year/week/:week" element={<RankingsPage />} />
              {/* Fantasy Points year routes without statCategory (must come before /rankings/:statCategory to avoid conflicts) */}
              <Route path="/rankings/:year" element={<RankingsPage />} />
              {/* Stat category routes (defaults to current year) */}
              <Route path="/rankings/:statCategory" element={<RankingsPage />} />
              {/* Base rankings route (defaults to Fantasy Points, current year) */}
              <Route path="/rankings" element={<RankingsPage />} />
              
              {/* Career hub — before /college/:slug/:statFilter so "career" is not a stat */}
              <Route path="/college/:slug/career/:statFilter" element={<CollegeCareerPage />} />
              <Route path="/college/:slug/career" element={<CollegeCareerPage />} />

              <Route path="/conferences/career/:abbr/:statFilter" element={<ConferenceCareerPage />} />
              <Route path="/conferences/career/:abbrOrStat" element={<ConferenceCareerPage />} />
              <Route path="/conferences/career" element={<ConferenceCareerPage />} />

              {/* College detail routes — CollegeDetailPage uses parseCollegeUrl to distinguish year vs stat */}
              <Route path="/college/:slug/:statFilter/:year/week/:week" element={<CollegeDetailPage />} />
              <Route path="/college/:slug/:statFilter/:year" element={<CollegeDetailPage />} />
              <Route path="/college/:slug/:statFilter" element={<CollegeDetailPage />} />
              <Route path="/college/:slug/:year/week/:week" element={<CollegeDetailPage />} />
              <Route path="/college/:slug/:year" element={<CollegeDetailPage />} />
              {/* Team hub: default season (Fantasy Points, full season) */}
              <Route path="/college/:slug" element={<CollegeDetailPage />} />
              
              {/* Class comparison and one-class breakdowns */}
              <Route path="/classes/:classYear/conference" element={<ClassDetailPage />} />
              <Route path="/classes/:classYear/:collegeSlug" element={<ClassDetailPage />} />
              <Route path="/classes/:classYear" element={<ClassDetailPage />} />
              <Route path="/classes" element={<ClassesPage />} />

              {/* Player career — before :statCategory so "career" is not a stat */}
              <Route path="/players/career/:statFilter" element={<PlayersCareerPage />} />
              <Route path="/players/career" element={<PlayersCareerPage />} />

              {/* Player leaderboards — rookies routes before :statCategory / :year */}
              <Route path="/players/rookies/:year/by-college" element={<PlayersPage />} />
              <Route path="/players/rookies/:year" element={<PlayersPage />} />
              <Route path="/players/:statCategory/:year/week/:week" element={<PlayersPage />} />
              <Route path="/players/:statCategory/:year" element={<PlayersPage />} />
              <Route path="/players/:year/week/:week" element={<PlayersPage />} />
              <Route path="/players/:year" element={<PlayersPage />} />
              <Route path="/players/:statCategory" element={<PlayersPage />} />
              <Route path="/players" element={<PlayersPage />} />

              {/* About page */}
              <Route path="/about" element={<About />} />
              {/* Data & Methodology (indexable source / method page) */}
              <Route path="/methodology" element={<Methodology />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </Router>
    </HelmetProvider>
  );
}

const container = document.getElementById("root");
if (container) {
  if (prerenderedHtmlMatchesUrl(container)) {
    // Keep the prerendered page on screen until the app has its data (see prerenderHandoff)
    createRoot(createHandoffContainer(container)).render(<App />);
  } else {
    // The app shell (empty #root), or saved HTML that doesn't belong to this URL
    container.replaceChildren();
    createRoot(container).render(<App />);
  }
}

